import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { Check, Trash2 } from "lucide-react";
import { translateScriptReviewFailedPass } from "@/features/script-review/lib/script-review-status";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@nexus/ui";

// ─── Public prop types (decoupled from the API layer so the same component
//     drives both the authed report page and the public guest page) ──────────

export interface PlayerAiComment {
  id: string;
  type: string;
  text: string;
  exactSecond: number;
  severity: string | null;
  sceneNumber: number | null;
  /** Readiness: set when the thread was marked addressed. Drives resolved styling. */
  resolvedAt?: string | null;
  resolvedByName?: string | null;
}

export interface PlayerNote {
  id: string;
  aiCommentId: string | null;
  anchorSecond: number | null;
  authorName: string;
  authorKind?: string;
  body: string;
  resolvedAt?: string | null;
  resolvedByName?: string | null;
  createdAt: string;
}

export type PlayerNoteTarget = { aiCommentId: string } | { anchorSecond: number };

export interface ScriptReviewPlayerProps {
  /** Signed video URL; null/expired → poster + reload hint (comments still render). */
  videoUrl: string | null;
  aiComments: PlayerAiComment[];
  notes: PlayerNote[];
  /** Fallback timeline length until the <video> reports its real duration. */
  durationHint?: number;
  canComment: boolean;
  /** Allow dropping standalone comments anywhere on the timeline (full Frame.io). */
  allowStandaloneNotes?: boolean;
  /** Client org reviewers: standalone timeline notes only — no AI-thread replies. */
  clientNotesOnly?: boolean;
  /** Persist a note. Resolves once saved; the parent refetches to show it. */
  onPostNote: (name: string, body: string, target: PlayerNoteTarget) => Promise<void>;
  /** Prefill the reviewer name (authed page passes the Adge display name). */
  currentUserNameDefault?: string;
  /**
   * Adge-admin-only moderation. When a callback is provided a delete affordance
   * appears — on AI comment cards (onDeleteComment) and on note cards + replies
   * (onDeleteNote). The public guest page passes neither, so the delete UI is
   * impossible there by construction. Each callback does the API call + refetch
   * + toast; it should reject on failure so the confirm dialog stays open.
   * `reason` (AI comments only) spawns a prompt-improvement ticket server-side.
   */
  onDeleteComment?: (commentId: string, reason?: string) => Promise<void>;
  onDeleteNote?: (noteId: string) => Promise<void>;
  /**
   * Resolve toggle for ops and vendor partners — same render-only-when-passed
   * contract as delete callbacks. The public guest page passes neither.
   */
  onResolveComment?: (commentId: string, resolved: boolean) => Promise<void>;
  onResolveNote?: (noteId: string, resolved: boolean) => Promise<void>;
  /**
   * Comment-pass names that failed after retries (`voiceover` / `text-overlay` /
   * `visual`). Empty or omitted when every pass succeeded.
   */
  failedPasses?: string[];
}

const NAME_STORAGE_KEY = "adge.reviewer.name";

function formatResolvedTimestamp(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(iso),
  );
}

// ─── Per comment-type visual identity ───────────────────────────────────────
// One source of truth for the color-coding shown on the timeline markers, the
// sidebar type badges, and the filter chips. AI comment types + reviewer notes.
type CommentCategory = "VOICEOVER" | "TEXT_OVERLAY" | "VISUAL" | "NOTE";

interface CategoryMeta {
  label: string;
  /** Solid swatch — timeline markers + legend/filter dots. */
  dot: string;
  /** Soft pill — the sidebar type badge. */
  badge: string;
}

interface CategoryStyle {
  dot: string;
  badge: string;
}

const CATEGORY_STYLE: Record<CommentCategory, CategoryStyle> = {
  VOICEOVER: {
    dot: "bg-violet-500",
    badge: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
  },
  TEXT_OVERLAY: {
    dot: "bg-amber-500",
    badge: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  },
  VISUAL: {
    dot: "bg-emerald-500",
    badge: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  },
  NOTE: {
    dot: "bg-sky-500",
    badge: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  },
};

const CATEGORY_LABEL_KEYS: Record<CommentCategory, string> = {
  VOICEOVER: "scriptReview.player.categories.voiceover",
  TEXT_OVERLAY: "scriptReview.player.categories.textOverlay",
  VISUAL: "scriptReview.player.categories.visual",
  NOTE: "scriptReview.player.categories.note",
};

const CATEGORY_LABEL_DEFAULTS: Record<CommentCategory, string> = {
  VOICEOVER: "Voiceover",
  TEXT_OVERLAY: "On-screen text",
  VISUAL: "Visual",
  NOTE: "Reviewer",
};

const CATEGORY_ORDER: CommentCategory[] = ["VOICEOVER", "TEXT_OVERLAY", "VISUAL", "NOTE"];

const FALLBACK_STYLE: CategoryStyle = {
  dot: "bg-muted-foreground",
  badge: "border-border bg-muted text-muted-foreground",
};

function metaFor(
  t: (key: string, defaultValue: string) => string,
  category: string,
): CategoryMeta {
  const cat = category as CommentCategory;
  const style = CATEGORY_STYLE[cat];
  if (style) {
    return {
      label: t(CATEGORY_LABEL_KEYS[cat], CATEGORY_LABEL_DEFAULTS[cat]),
      ...style,
    };
  }
  return {
    label: t("scriptReview.player.categories.fallback", "Comment"),
    ...FALLBACK_STYLE,
  };
}

function formatSecond(s: number): string {
  const total = Math.max(0, Math.round(s));
  return `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, "0")}`;
}

function readStoredName(): string {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem(NAME_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

/** A timeline anchor: either an AI comment (with its replies) or a standalone note. */
type Thread =
  | { key: string; kind: "ai"; second: number; comment: PlayerAiComment; replies: PlayerNote[] }
  | { key: string; kind: "note"; second: number; note: PlayerNote };

/** The color-coded category a thread belongs to (AI comment type, or reviewer note). */
function categoryOf(thread: Thread): string {
  return thread.kind === "ai" ? thread.comment.type : "NOTE";
}

/** Readiness: a thread is resolved via its own item — replies never carry it. */
function isResolved(thread: Thread): boolean {
  const at = thread.kind === "ai" ? thread.comment.resolvedAt : thread.note.resolvedAt;
  return at != null;
}

export function ScriptReviewPlayer({
  videoUrl,
  aiComments,
  notes,
  durationHint,
  canComment,
  allowStandaloneNotes = false,
  clientNotesOnly = false,
  onPostNote,
  currentUserNameDefault,
  onDeleteComment,
  onDeleteNote,
  onResolveComment,
  onResolveNote,
  failedPasses = [],
}: ScriptReviewPlayerProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [duration, setDuration] = useState(durationHint ?? 0);
  const [currentTime, setCurrentTime] = useState(0);
  const [name, setName] = useState(() => currentUserNameDefault || readStoredName());
  const [activeKey, setActiveKey] = useState<string | null>(null);
  // Comment panel is height-matched to the video (desktop) so it scrolls on its own.
  const [panelHeight, setPanelHeight] = useState<number | null>(null);
  const [isDesktop, setIsDesktop] = useState(false);

  const threads = useMemo<Thread[]>(() => {
    const repliesByComment = new Map<string, PlayerNote[]>();
    const standalone: PlayerNote[] = [];
    for (const n of notes) {
      if (n.aiCommentId) {
        let arr = repliesByComment.get(n.aiCommentId);
        if (!arr) {
          arr = [];
          repliesByComment.set(n.aiCommentId, arr);
        }
        arr.push(n);
      } else if (n.anchorSecond != null) {
        standalone.push(n);
      }
    }
    const list: Thread[] = [
      ...aiComments.map((comment) => ({
        key: `ai:${comment.id}`,
        kind: "ai" as const,
        second: comment.exactSecond,
        comment,
        replies: repliesByComment.get(comment.id) ?? [],
      })),
      ...standalone.map((note) => ({
        key: `note:${note.id}`,
        kind: "note" as const,
        second: note.anchorSecond ?? 0,
        note,
      })),
    ];
    return list.sort((a, b) => a.second - b.second);
  }, [aiComments, notes]);

  // ── Filter by comment type (drives both the timeline + the sidebar) ──────
  const [hiddenCats, setHiddenCats] = useState<Set<string>>(() => new Set());
  const toggleCat = (cat: string) =>
    setHiddenCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });

  const { presentCats, counts } = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const th of threads) {
      const c = categoryOf(th);
      counts[c] = (counts[c] ?? 0) + 1;
    }
    return { presentCats: CATEGORY_ORDER.filter((c) => (counts[c] ?? 0) > 0), counts };
  }, [threads]);

  // Readiness: an opt-in filter so a long resolved backlog doesn't bury the
  // remaining work. Off by default so nothing silently disappears.
  const [hideResolved, setHideResolved] = useState(false);
  const resolvedCount = useMemo(() => threads.filter(isResolved).length, [threads]);

  const visibleThreads = useMemo(
    () =>
      threads.filter(
        (th) => !hiddenCats.has(categoryOf(th)) && !(hideResolved && isResolved(th)),
      ),
    [threads, hiddenCats, hideResolved],
  );

  const timelineMax = duration > 0 ? duration : Math.max(1, ...threads.map((t) => t.second), durationHint ?? 0);

  // Track desktop breakpoint so the exact-height match only applies side-by-side.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Match the panel height to the live video height (updates on resize / metadata).
  useEffect(() => {
    const el = videoRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      const h = el.clientHeight;
      if (h > 0) setPanelHeight(h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [videoUrl]);

  // Whenever the active comment changes (click OR playback-follow), bring it into
  // view within the panel — only if it's off-screen, so it never fights scrolling.
  useEffect(() => {
    if (!activeKey || !scrollRef.current) return;
    const esc = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(activeKey) : activeKey;
    const el = scrollRef.current.querySelector<HTMLElement>(`[data-thread-key="${esc}"]`);
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeKey]);

  const seekTo = (second: number, key?: string) => {
    const v = videoRef.current;
    if (v && Number.isFinite(second)) v.currentTime = Math.max(0, second);
    if (key) setActiveKey(key);
  };

  // As the playhead moves, highlight the comment at/just before it (Frame.io-style).
  const handleTimeUpdate = (t: number) => {
    setCurrentTime(t);
    let currentKey: string | null = null;
    for (const th of visibleThreads) {
      if (th.second <= t + 0.25) currentKey = th.key;
      else break; // threads are sorted by second
    }
    if (currentKey && currentKey !== activeKey) setActiveKey(currentKey);
  };

  const persistName = (value: string) => {
    setName(value);
    try {
      window.localStorage.setItem(NAME_STORAGE_KEY, value.trim());
    } catch {
      /* ignore storage failures */
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {failedPasses.length > 0 && (
        <div
          role="alert"
          className="rounded-md border border-warning-border bg-warning-subtle px-3 py-2 text-sm text-warning-subtle-foreground"
        >
          {t("scriptReview.player.failedPasses", {
            count: failedPasses.length,
            passes: failedPasses.map((step) => translateScriptReviewFailedPass(t, step)).join(", "),
            defaultValue_one:
              "{{passes}} analysis couldn't finish. Other comments are still available.",
            defaultValue_other:
              "{{passes}} analyses couldn't finish. Other comments are still available.",
          })}
        </div>
      )}
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      {/* ── Left: video + timeline ─────────────────────────────────────── */}
      <div className="flex flex-1 flex-col gap-2">
        {videoUrl ? (
          <video
            ref={videoRef}
            src={videoUrl}
            controls
            playsInline
            className="w-full rounded-lg border border-border bg-black"
            onLoadedMetadata={(e) => {
              const d = e.currentTarget.duration;
              if (Number.isFinite(d) && d > 0) setDuration(d);
            }}
            onTimeUpdate={(e) => handleTimeUpdate(e.currentTarget.currentTime)}
          />
        ) : (
          <div className="flex aspect-video w-full items-center justify-center rounded-lg border border-border bg-muted text-center text-sm text-muted-foreground">
            {t(
              "scriptReview.player.videoUnavailable",
              "Video unavailable — the link may have expired. Reload the page to refresh it.",
            )}
          </div>
        )}

        {/* Timeline with comment markers */}
        <div className="relative h-8 w-full rounded-md border border-border bg-muted">
          <div
            className="absolute inset-y-0 left-0 rounded-l-md bg-primary/20"
            style={{ width: `${Math.min(100, (currentTime / timelineMax) * 100)}%` }}
          />
          {visibleThreads.map((thread) => (
            <button
              key={thread.key}
              type="button"
              title={`${formatSecond(thread.second)} · ${thread.kind === "ai" ? metaFor(t, thread.comment.type).label : thread.note.authorName}`}
              onClick={() => seekTo(thread.second, thread.key)}
              style={{ left: `${Math.min(100, (thread.second / timelineMax) * 100)}%` }}
              className={cn(
                "absolute top-1/2 h-4 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full transition-transform hover:scale-125",
                metaFor(t, categoryOf(thread)).dot,
                // Resolved markers recede so the timeline reads as remaining work.
                isResolved(thread) && "opacity-40",
                activeKey === thread.key && "z-10 scale-[1.6] ring-2 ring-primary/70",
              )}
            />
          ))}
        </div>

        {/* Legend + filter: click a type to show/hide it on the timeline and list */}
        {presentCats.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {presentCats.map((cat) => {
              const meta = metaFor(t, cat);
              const active = !hiddenCats.has(cat);
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => toggleCat(cat)}
                  aria-pressed={active}
                  title={
                    active
                      ? t("scriptReview.player.hideComments", "Hide {{label}} comments", { label: meta.label })
                      : t("scriptReview.player.showComments", "Show {{label}} comments", { label: meta.label })
                  }
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs transition-colors",
                    active
                      ? "border-border bg-card text-foreground hover:bg-muted/50"
                      : "border-transparent bg-muted/40 text-muted-foreground opacity-70 hover:opacity-100",
                  )}
                >
                  <span className={cn("inline-block h-2 w-2 rounded-full", meta.dot, !active && "opacity-40")} />
                  <span className={cn(!active && "line-through")}>{meta.label}</span>
                  <span className="tabular-nums text-muted-foreground">{counts[cat] ?? 0}</span>
                </button>
              );
            })}
            {resolvedCount > 0 && (
              <button
                type="button"
                onClick={() => setHideResolved((v) => !v)}
                aria-pressed={hideResolved}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs transition-colors",
                  hideResolved
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                    : "border-border bg-card text-muted-foreground hover:bg-muted/50",
                )}
              >
                <Check className="h-3 w-3" aria-hidden />
                <span>{t("scriptReview.resolve.hideResolved", "Hide resolved")}</span>
                <span className="tabular-nums text-muted-foreground">{resolvedCount}</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Right: comment panel (height-matched to the video, scrolls on its own) ── */}
      <div
        className="flex w-full flex-col gap-3 lg:max-w-md"
        style={isDesktop && panelHeight ? { height: panelHeight } : undefined}
      >
        {canComment && (
          <div className="flex shrink-0 flex-col gap-2">
            <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
              {t("scriptReview.player.commentingAs", "Commenting as")}
              <input
                value={name}
                onChange={(e) => persistName(e.target.value)}
                placeholder={t("scriptReview.player.namePlaceholder", "Your name")}
                maxLength={80}
                className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              />
            </label>
            {allowStandaloneNotes && (
              <StandaloneComposer
                disabled={!name.trim()}
                atSecond={currentTime}
                onSubmit={async (body) => {
                  await onPostNote(name.trim(), body, { anchorSecond: currentTime });
                  persistName(name);
                }}
              />
            )}
          </div>
        )}

        <div
          ref={scrollRef}
          className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-1 max-lg:max-h-[65vh]"
        >
          {visibleThreads.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {threads.length === 0
                ? t("scriptReview.player.emptyTimeline", "No comments on the timeline yet.")
                : t("scriptReview.player.emptyFilter", "No comments match the current filter.")}
            </p>
          )}
          {visibleThreads.map((thread) =>
            thread.kind === "ai" ? (
              <ThreadCard
                key={thread.key}
                threadKey={thread.key}
                active={activeKey === thread.key}
                second={thread.second}
                onSeek={() => seekTo(thread.second, thread.key)}
                header={
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    <span
                      className={cn(
                        "rounded border px-1.5 py-0.5 text-[11px] font-medium",
                        metaFor(t, thread.comment.type).badge,
                      )}
                    >
                      {metaFor(t, thread.comment.type).label}
                    </span>
                    {thread.comment.sceneNumber != null && (
                      <span className="text-muted-foreground">
                        {t("scriptReview.player.scene", "Scene {{number}}", {
                          number: thread.comment.sceneNumber,
                        })}
                      </span>
                    )}
                    {thread.comment.severity && (
                      <span className="text-muted-foreground">· {thread.comment.severity}</span>
                    )}
                  </span>
                }
                body={thread.comment.text}
                replies={thread.replies}
                composer={
                  canComment && !clientNotesOnly
                    ? {
                        disabled: !name.trim(),
                        onSubmit: async (body) => {
                          await onPostNote(name.trim(), body, { aiCommentId: thread.comment.id });
                          persistName(name);
                        },
                      }
                    : undefined
                }
                onDelete={
                  onDeleteComment
                    ? (reason?: string) => onDeleteComment(thread.comment.id, reason)
                    : undefined
                }
                deleteKind="comment"
                onDeleteReply={onDeleteNote ? (noteId) => onDeleteNote(noteId) : undefined}
                resolved={isResolved(thread)}
                resolvedAt={thread.comment.resolvedAt}
                resolvedByName={thread.comment.resolvedByName}
                onResolve={
                  onResolveComment
                    ? (resolved) => onResolveComment(thread.comment.id, resolved)
                    : undefined
                }
              />
            ) : (
              <ThreadCard
                key={thread.key}
                threadKey={thread.key}
                active={activeKey === thread.key}
                second={thread.second}
                onSeek={() => seekTo(thread.second, thread.key)}
                header={
                  <span className="inline-flex items-center gap-1.5">
                    <span className={cn("inline-block h-2 w-2 rounded-full", CATEGORY_STYLE.NOTE.dot)} />
                    <span className="font-medium">{thread.note.authorName}</span>
                  </span>
                }
                body={thread.note.body}
                replies={[]}
                onDelete={onDeleteNote ? () => onDeleteNote(thread.note.id) : undefined}
                deleteKind="note"
                resolved={isResolved(thread)}
                resolvedAt={thread.note.resolvedAt}
                resolvedByName={thread.note.resolvedByName}
                onResolve={
                  onResolveNote ? (resolved) => onResolveNote(thread.note.id, resolved) : undefined
                }
              />
            ),
          )}
        </div>
      </div>
    </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────

function ThreadCard({
  threadKey,
  active,
  second,
  onSeek,
  header,
  body,
  replies,
  composer,
  onDelete,
  deleteKind,
  onDeleteReply,
  resolved = false,
  resolvedAt = null,
  resolvedByName = null,
  onResolve,
}: {
  threadKey: string;
  active: boolean;
  second: number;
  onSeek: () => void;
  header: ReactNode;
  body: string;
  replies: PlayerNote[];
  composer?: { disabled: boolean; onSubmit: (body: string) => Promise<void> };
  /** Adge-admin delete of this card's own item; kind drives the confirm copy.
      For AI comments the confirm dialog may pass a rejection reason through. */
  onDelete?: (reason?: string) => Promise<void>;
  deleteKind?: "comment" | "note";
  /** Adge-admin delete of a specific reply under an AI comment. */
  onDeleteReply?: (noteId: string) => Promise<void>;
  /** Readiness: whether this thread is resolved (drives dimming + the chip). */
  resolved?: boolean;
  resolvedAt?: string | null;
  resolvedByName?: string | null;
  /** Resolve toggle; absent → no control (guests see state only). */
  onResolve?: (resolved: boolean) => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const [replying, setReplying] = useState(false);

  /**
   * The whole card jumps the video to its timestamp — a comment is a much bigger
   * target than the little time chip, and jumping is what you want ~every time
   * you touch one. The card stays a <div>: it holds buttons, and a <button>
   * can't contain buttons.
   *
   * Two kinds of click are NOT a seek: one that lands on a control (resolve,
   * delete, reply, the composer — that control's business), and one that ends a
   * text selection (someone copying the comment, not asking to move the video).
   * Testing the target rather than stopping propagation in each child means a
   * control added here later is handled without anyone remembering to.
   */
  function handleCardClick(e: ReactMouseEvent<HTMLDivElement>) {
    if ((e.target as HTMLElement).closest("button, a, input, textarea, select, label")) return;
    if (window.getSelection()?.toString()) return;
    onSeek();
  }

  return (
    <div
      data-thread-key={threadKey}
      onClick={handleCardClick}
      className={cn(
        "flex scroll-mt-2 cursor-pointer flex-col gap-2 rounded-lg border p-3 text-sm transition-colors",
        active
          ? "border-primary bg-primary/5 ring-1 ring-primary/40"
          : "border-border bg-card hover:border-primary/40",
        // Resolved threads recede but stay readable + fully interactive.
        resolved && !active && "opacity-60",
      )}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={onSeek}
          className={cn(
            "shrink-0 rounded px-1.5 py-0.5 font-mono text-xs transition-colors",
            active ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground hover:text-foreground",
          )}
        >
          {formatSecond(second)}
        </button>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="inline-flex flex-wrap items-center gap-1.5 text-xs">
            {header}
            {resolved && (
              <span className="rounded border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                {t("scriptReview.resolve.chip", "Resolved")}
              </span>
            )}
          </span>
          <span className="whitespace-pre-wrap">{body}</span>
          {resolved && resolvedAt && (
            <span className="text-xs text-muted-foreground">
              {resolvedByName
                ? t("scriptReview.resolve.resolvedBy", "Resolved by {{name}} on {{date}}", {
                    name: resolvedByName,
                    date: formatResolvedTimestamp(resolvedAt, i18n.language),
                  })
                : t("scriptReview.resolve.resolvedOn", "Resolved on {{date}}", {
                    date: formatResolvedTimestamp(resolvedAt, i18n.language),
                  })}
            </span>
          )}
        </div>
        {onResolve && <ResolveToggleButton resolved={resolved} onToggle={onResolve} />}
        {onDelete && deleteKind && (
          <ConfirmDeleteButton onConfirm={onDelete} kind={deleteKind} />
        )}
      </div>

      {replies.length > 0 && (
        <ul className="flex flex-col gap-2 border-l border-border pl-3">
          {replies.map((r) => (
            <li key={r.id} className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-xs font-medium text-muted-foreground">{r.authorName}</span>
                <span className="whitespace-pre-wrap">{r.body}</span>
              </div>
              {onDeleteReply && (
                <ConfirmDeleteButton onConfirm={() => onDeleteReply(r.id)} kind="reply" compact />
              )}
            </li>
          ))}
        </ul>
      )}

      {composer &&
        (replying ? (
          <InlineComposer
            disabled={composer.disabled}
            placeholder={t("scriptReview.player.replyPlaceholder", "Write a reply…")}
            onCancel={() => setReplying(false)}
            onSubmit={async (b) => {
              await composer.onSubmit(b);
              setReplying(false);
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setReplying(true)}
            className="self-start text-xs font-medium text-primary hover:underline"
          >
            {t("scriptReview.player.reply", "Reply")}
          </button>
        ))}
    </div>
  );
}

/**
 * Adge-admin resolve toggle: marks a thread addressed for readiness scoring.
 * No confirm dialog — unlike delete this is instantly reversible. Disabled while
 * in-flight so a double-click can't fire two opposite writes.
 */
function ResolveToggleButton({
  resolved,
  onToggle,
}: {
  resolved: boolean;
  onToggle: (resolved: boolean) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const label = resolved
    ? t("scriptReview.resolve.unresolveAria", "Mark unresolved")
    : t("scriptReview.resolve.resolveAria", "Mark resolved");

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={resolved}
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await onToggle(!resolved);
        } catch {
          /* parent surfaces the error toast */
        } finally {
          setBusy(false);
        }
      }}
      className={cn(
        "shrink-0 rounded p-1 transition-colors disabled:opacity-50",
        resolved
          ? "text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400"
          : "text-muted-foreground hover:bg-emerald-500/10 hover:text-emerald-600",
      )}
    >
      <Check className="h-3.5 w-3.5" aria-hidden />
    </button>
  );
}

/**
 * Adge-admin destructive delete: a small trash button that opens a confirm dialog
 * and awaits `onConfirm` (the parent does the API call + refetch + toast, and
 * rejects on failure so the dialog stays open for retry).
 */
function ConfirmDeleteButton({
  onConfirm,
  kind,
  compact = false,
}: {
  /** Receives the optional rejection reason (AI comments only). */
  onConfirm: (reason?: string) => Promise<void>;
  kind: "comment" | "note" | "reply";
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Rejection reason — AI comments only. A reason spawns a prompt-improvement
  // ticket so the pipeline learns from the mistake; empty = plain delete.
  const [reason, setReason] = useState("");

  const copy = {
    comment: {
      aria: t("scriptReview.delete.ariaComment", "Delete comment"),
      title: t("scriptReview.delete.titleComment", "Delete comment"),
      description: t(
        "scriptReview.delete.descriptionComment",
        "This comment and any replies will be permanently deleted. This can't be undone.",
      ),
    },
    note: {
      aria: t("scriptReview.delete.ariaNote", "Delete note"),
      title: t("scriptReview.delete.titleNote", "Delete note"),
      description: t(
        "scriptReview.delete.descriptionNote",
        "This note will be permanently deleted. This can't be undone.",
      ),
    },
    reply: {
      aria: t("scriptReview.delete.ariaReply", "Delete reply"),
      title: t("scriptReview.delete.titleReply", "Delete reply"),
      description: t(
        "scriptReview.delete.descriptionReply",
        "This reply will be permanently deleted. This can't be undone.",
      ),
    },
  }[kind];

  const confirm = async () => {
    setBusy(true);
    try {
      const trimmed = reason.trim();
      // Server requires >= 4 chars; shorter scribbles are treated as no reason.
      await onConfirm(kind === "comment" && trimmed.length >= 4 ? trimmed : undefined);
      setOpen(false);
      setReason("");
    } catch {
      /* parent surfaces the error toast; keep the dialog open for retry */
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        aria-label={copy.aria}
        title={copy.aria}
        onClick={() => setOpen(true)}
        className={cn(
          "shrink-0 rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive",
          compact ? "p-0.5" : "p-1",
        )}
      >
        <Trash2 className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} aria-hidden />
      </button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setReason("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{copy.title}</DialogTitle>
            <DialogDescription>{copy.description}</DialogDescription>
          </DialogHeader>
          {kind === "comment" && (
            <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
              {t("scriptReview.delete.reasonLabel", "Why is this comment wrong? (optional)")}
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={1000}
                disabled={busy}
                placeholder={t(
                  "scriptReview.delete.reasonPlaceholder",
                  "e.g. The caption was present — the AI missed it",
                )}
                className="w-full resize-none rounded-md border border-border bg-background px-2 py-1.5 text-sm font-normal text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              />
              <span className="font-normal">
                {t(
                  "scriptReview.delete.reasonHint",
                  "Your reason trains the AI — it drafts a prompt fix for admins to review.",
                )}
              </span>
            </label>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button variant="destructive" onClick={confirm} disabled={busy}>
              {busy
                ? t("scriptReview.delete.deleting", "Deleting…")
                : t("scriptReview.delete.confirm", "Delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StandaloneComposer({
  disabled,
  atSecond,
  onSubmit,
}: {
  disabled: boolean;
  atSecond: number;
  onSubmit: (body: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <Button variant="outline" size="sm" className="self-start" onClick={() => setOpen(true)}>
        {t("scriptReview.player.addCommentAt", "+ Comment at {{time}}", { time: formatSecond(atSecond) })}
      </Button>
    );
  }
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <p className="mb-1 text-xs text-muted-foreground">
        {t("scriptReview.player.newCommentAt", "New comment at {{time}}", { time: formatSecond(atSecond) })}
      </p>
      <InlineComposer
        disabled={disabled}
        placeholder={t(
          "scriptReview.player.standalonePlaceholder",
          "Add a comment at the current time…",
        )}
        onCancel={() => setOpen(false)}
        onSubmit={async (b) => {
          await onSubmit(b);
          setOpen(false);
        }}
      />
    </div>
  );
}

function InlineComposer({
  disabled,
  placeholder,
  onSubmit,
  onCancel,
}: {
  disabled: boolean;
  placeholder: string;
  onSubmit: (body: string) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const trimmed = body.trim();
    if (!trimmed || disabled || busy) return;
    setBusy(true);
    try {
      await onSubmit(trimmed);
      setBody("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-2">
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={
          disabled
            ? t("scriptReview.player.nameRequiredPlaceholder", "Enter your name above first")
            : placeholder
        }
        rows={3}
        maxLength={4000}
        className="w-full resize-y rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
      />
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={submit} disabled={disabled || busy || !body.trim()}>
          {busy ? t("scriptReview.player.posting", "Posting…") : t("scriptReview.player.post", "Post")}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t("common.cancel", "Cancel")}
        </Button>
      </div>
    </div>
  );
}
